/* eslint "ember/no-get":off */

import { registerDestructor } from '@ember/destroyable';
import { later } from '@ember/runloop';
import { resolve, Promise } from 'rsvp';
import EmberObject, { computed } from '@ember/object';
import { warn } from '@ember/debug';
import $ from 'jquery';
import Ember from 'ember';
import { waitUntil } from '@ember/test-helpers';
import getZIndex from '@square/onboarding/utils/get-z-index';
import { waitForSheetAnimation } from '@square/onboarding/utils/wait-for-animation';
import Popper from 'popper.js';
import WalkthroughPopover from '@square/onboarding/components/walkthrough-popover';

/**

Walkthrough object used by the Walkthrough service. Walkthrough objects manage
the steps in a walkthrough and specify positioning, copy, and placement of each
popover step.

For information on how to create walkthroughs in Dashboard, check out the
[Walkthrough Popover Guide](/development/walkthrough.html).

### Usage

```js
const walkthrough = Walkthrough.create({
  steps: [
    new WalkthroughStep({
      name: TourSteps.APP_DRAWER,
      targetElement: '.app-drawer', // targetElement can be a selector or a function that returns a DOM element
      text: 'This is the app drawer!',
      buttonText: 'Next',
      placement: 'bottom-start',
      pointerEnabled: true,
    }),
    ...
  ],
  onStepComplete: onStepComplete, // optional function to call when a walkthrough step is completed
  onComplete: onComplete, // optional function to call when the walkthrough is completed
  shouldShowCloseButton: true, // default is false. If true, "X" buttons will show on the walkthrough popover steps
  onClose: onClose // optional function which is called when the walkthrough is closed by clicking the "X" button on one of the walkthrough steps
});
this.get('walkthroughService').start(walkthrough);
```
*/

// Should match $popover--animation-speed
const ANIMATION_DELAY = 300;
function getTimeout() {
  return Ember.testing ? 400 : 60000;
}
const POPOVER_MANAGER_CLASS = '.walkthrough-popover-manager';

// Assign popover manager a z-index that ensures popovers are layered correctly above and below other
// page elements within the app.
const POPOVER_MANAGER_Z_INDEX_OFFSET = 600;
export const FIXED_BOTTOM_RIGHT = Symbol('FIXED_BOTTOM_RIGHT');
function elementIsVisible(element) {
  if (!element) {
    return false;
  }
  const $element = $(element); // eslint-disable-line ember/no-jquery
  return $element.is(':visible') && $element.css('visibility') !== 'hidden' && $element.css('opacity') !== 0;
}

// eslint-disable-next-line ember/no-classic-classes
export default EmberObject.extend({
  isActive: true,
  currentStepIndex: 0,
  name: null,
  popper: null,
  popoverIsActive: false,
  popoverComponent: WalkthroughPopover,
  shouldShowCloseButton: false,
  autoScrollToTarget: false,
  isUserInitiated: false,
  onComplete: null,
  onClose: null,
  init(...args) {
    this._super(...args);
    if (!this.hideWhenTargetIsNotVisible) {
      this.set('hideWhenTargetIsNotVisible', !Ember.testing);
    }
  },
  _steps: null,
  /**
   * @property {WalkthroughStep[]} steps
   */
  get steps() {
    return this._steps ?? [];
  },
  set steps(newSteps) {
    this._steps = newSteps;
  },
  currentStep: computed('steps', 'currentStepIndex', function () {
    return this.steps[this.currentStepIndex];
  }),
  isComplete: computed('steps.length', 'currentStepIndex', function () {
    return this.currentStepIndex >= this.get('steps.length');
  }),
  getPopperElement() {
    if (this.popperElement) {
      return resolve(this.popperElement);
    } else {
      return waitUntil(() => document.querySelector(POPOVER_MANAGER_CLASS), {
        timeout: getTimeout()
      }).then(popperElement => {
        this.set('popperElement', popperElement);
        return popperElement;
      });
    }
  },
  start() {
    return this.setUpPopperForCurrentStep();
  },
  stop() {
    this.set('popoverIsActive', false);
    if (this.popper) {
      this.popper.destroy();
      this.set('popper', null);
    }
    return resolve();
  },
  // Watches the targetElement. If the target element is no longer visible, the popover is hidden.
  watchElement(targetElement) {
    if (this.hideWhenTargetIsNotVisible) {
      const $target = $(targetElement); // eslint-disable-line ember/no-jquery
      const currentStep = this.currentStep;
      return waitUntil(() => {
        // Resolve if the current step changes
        if (this.currentStep !== currentStep || this.isDestroyed) {
          return {
            shouldHidePopover: false
          };
        }
        if (!elementIsVisible($target)) {
          return {
            shouldHidePopover: true
          };
        }
        return false;
      }).then(({
        shouldHidePopover
      }) => {
        if (shouldHidePopover) {
          this.set('popoverIsActive', false);
          this.setUpPopperForCurrentStep(); // so popper will reappear when target element becomes visible again
        }
      });
    } else {
      return resolve();
    }
  },
  initializePopper(targetElement) {
    return this.getPopperElement().then(popperElement => {
      if (targetElement === FIXED_BOTTOM_RIGHT) {
        return this._initializeFixedPopper();
      }
      return this._initializeDefaultPopper(targetElement, popperElement);
    });
  },
  _initializeDefaultPopper(targetElement, popperElement) {
    return new Promise(resolve => {
      // when overwriting an existing popper, call destroy to do cleanup and prevent popper from getting in a weird state
      if (this.popper) {
        this.popper.destroy();
      }
      const popper = new Popper(targetElement, popperElement, this.currentStep.buildPopperOptions({
        onCreate: () => {
          this.set('popoverIsActive', true);
          // Popovers are generally rendered at the default popover z-index (600).
          // For popovers attached to elements with a higher z-index (sheets, modals) we use the target element's z-index + 1.

          /* eslint-disable ember/no-jquery */
          const zIndex = getZIndex(targetElement) || 0;
          $(popperElement).css('z-index', Math.max(POPOVER_MANAGER_Z_INDEX_OFFSET, zIndex + 1));
          if (this.autoScrollToTarget) {
            later(() => {
              targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }, 500);
          }

          /* eslint-enable ember/no-jquery */
          this.watchElement(targetElement);
          resolve();
        }
      }));

      // Ensure Popper is destroyed along with the walkthrough to prevent memory leaks in tests
      registerDestructor(this, () => {
        popper.destroy();
      });
      this.set('popper', popper);
    });
  },
  _initializeFixedPopper() {
    return new Promise(resolve => {
      this.set('popoverIsActive', true);
      /* eslint-disable ember/no-jquery */
      $('.walkthrough-popover-manager').css('z-index', POPOVER_MANAGER_Z_INDEX_OFFSET);
      /* eslint-enable ember/no-jquery */
      resolve();
    });
  },
  setUpPopperForCurrentStep() {
    return waitUntil(() => {
      const targetElementParam = this.get('currentStep.targetElement');
      if (targetElementParam === FIXED_BOTTOM_RIGHT) {
        return FIXED_BOTTOM_RIGHT;
      } else {
        let target;
        if (typeof targetElementParam === 'function') {
          target = targetElementParam();
        } else {
          target = document.querySelector(targetElementParam);
        }
        return elementIsVisible(target) ? target : false;
      }
    }, {
      timeout: getTimeout()
    }).then(targetElement => {
      return waitForSheetAnimation().then(() => {
        return this.initializePopper(targetElement);
      });
    }).catch(() => {
      if (!this.isDestroyed) {
        warn(`Could not attach popper to targetElement ${this.get('currentStep.targetElement')}`, {
          id: 'walkthrough.targetElement'
        });
      }
      return resolve();
    });
  },
  completeStep(name) {
    if (!this.popoverIsActive || this.isComplete || this.get('currentStep.name') !== name) {
      return resolve();
    }
    this.set('popoverIsActive', false);
    return new Promise(resolve => {
      later(() => {
        if (this.isDestroyed) {
          return;
        }
        if (this.get('currentStep.targetElement') !== FIXED_BOTTOM_RIGHT) {
          this.popper.destroy();
          this.set('popper', null);
        }
        this.incrementProperty('currentStepIndex');
        if (this.onStepComplete) {
          this.onStepComplete(name);
        }
        if (this.isComplete) {
          if (this.onComplete) {
            resolve(this.onComplete());
          }
        } else {
          resolve(this.setUpPopperForCurrentStep());
        }
      }, ANIMATION_DELAY);
    });
  }
});